import { Toast as Base } from "@base-ui/react/toast";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import "./surface.css";
import "./tones.css";
import "./toast.css";
import "./focus.css";
import { cn } from "./cn";
import type { Tone } from "./tone";

export type ToastTone = Tone;

/** Custom per-toast data carried through Base UI's manager (drives the tone accent). */
export interface ToastData {
  tone?: ToastTone;
}

/** Imperative API: `const toast = useToast(); toast.add({ title, description, data: { tone } })`. */
export function useToast() {
  return Base.useToastManager<ToastData>();
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="m4 4 8 8M12 4l-8 8" strokeLinecap="round" />
    </svg>
  );
}

function ToastList() {
  const { toasts } = useToast();
  const { t } = useTranslation("shared");
  return toasts.map((toast) => (
    <Base.Root
      key={toast.id}
      toast={toast}
      className={cn("rc-surface", `rc-tone-${toast.data?.tone ?? "neutral"}`, "rc-toast")}
    >
      <div className="rc-toast__content">
        <Base.Title className="rc-toast__title" />
        <Base.Description className="rc-toast__description" />
      </div>
      <Base.Close className="rc-toast__close" aria-label={t("toast.dismiss")}>
        <CloseIcon />
      </Base.Close>
    </Base.Root>
  ));
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Auto-dismiss delay in ms (0 disables). */
  timeout?: number;
  /** Max simultaneous toasts before the oldest is dropped. */
  limit?: number;
}

/**
 * Token-styled toast system on Base UI's headless Toast. Wrap the app once; trigger toasts with
 * `useToast().add(...)`. Auto-dismiss + manual close + swipe come from Base UI; the tone accent
 * comes from our slots. Render it near the app root.
 */
export function ToastProvider({ children, timeout, limit }: ToastProviderProps) {
  return (
    <Base.Provider timeout={timeout} limit={limit}>
      {children}
      <Base.Viewport className="rc-toast-viewport">
        <ToastList />
      </Base.Viewport>
    </Base.Provider>
  );
}
